import { create } from 'zustand';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { api } from '../lib/api';
import { config } from '../config';
import { useAccountStore } from './useAccount';
import { usePositionsStore } from './usePositions';
import type { AuthState, Profile } from '../types/user';

// Generate a fake email from username for Supabase auth
const usernameToEmail = (username: string) => `${username.toLowerCase()}@hypersim.local`;

interface ProfileRow {
  id: string;
  user_id: string;
  username: string;
  avatar_url?: string | null;
  created_at: string;
}

const mapProfile = (profile: ProfileRow): Profile => ({
  id: profile.id,
  userId: profile.user_id,
  username: profile.username,
  avatarUrl: profile.avatar_url || undefined,
  createdAt: profile.created_at,
});

interface AuthStore extends AuthState {
  initialize: () => Promise<void>;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setError: (error: string | null) => void;
}

let authSubscription: { unsubscribe: () => void } | null = null;
let authOperationGeneration = 0;

type LocalAuthOperationKind = 'login' | 'register' | 'logout';

interface LocalAuthOperation {
  generation: number;
  kind: LocalAuthOperationKind;
}

interface PendingSessionHydration {
  generation: number;
  subject: string;
  session: Session;
}

let localAuthOperation: LocalAuthOperation | null = null;
let pendingSessionHydration: PendingSessionHydration | null = null;
const inFlightSessionCreators = new Set<Promise<unknown>>();
let logoutEventBarrierCount = 0;
let activeLogoutBarrier: Promise<void> | null = null;
let activeSessionCreatorBarrier: Promise<void> | null = null;

export class AuthOperationSupersededError extends Error {
  constructor() {
    super('Authentication operation was superseded');
    this.name = 'AuthOperationSupersededError';
  }
}

const beginAuthOperation = () => {
  authOperationGeneration += 1;
  pendingSessionHydration = null;
  return authOperationGeneration;
};

const beginLocalAuthOperation = (kind: LocalAuthOperationKind) => {
  const generation = beginAuthOperation();
  localAuthOperation = { generation, kind };
  return generation;
};

const releaseLocalAuthOperation = (generation: number) => {
  if (localAuthOperation?.generation === generation) {
    localAuthOperation = null;
  }
};

const trackSessionCreator = <T>(request: Promise<T>) => {
  const trackedRequest = request as Promise<unknown>;
  inFlightSessionCreators.add(trackedRequest);
  void request
    .finally(() => {
      inFlightSessionCreators.delete(trackedRequest);
    })
    .catch(() => undefined);
  return request;
};

const tryProviderSignOut = async (
  scope: 'global' | 'local' = 'global'
): Promise<unknown | null> => {
  try {
    const { error } = await supabase.auth.signOut({ scope });
    return error;
  } catch (error) {
    return error;
  }
};

const isCurrentAuthOperation = (generation: number) =>
  generation === authOperationGeneration;

const assertCurrentAuthOperation = (generation: number) => {
  if (!isCurrentAuthOperation(generation)) {
    throw new AuthOperationSupersededError();
  }
};

const clearPriorProviderSession = async (operation: number) => {
  // Ignore the SIGNED_OUT callback generated by this deliberate cleanup. The
  // queued login/register intent, not the provider callback, owns the transition.
  logoutEventBarrierCount += 1;
  try {
    let signOutError = await tryProviderSignOut('local');
    assertCurrentAuthOperation(operation);
    if (signOutError) {
      signOutError = await tryProviderSignOut('local');
      assertCurrentAuthOperation(operation);
    }
    if (signOutError) throw signOutError;
  } finally {
    logoutEventBarrierCount -= 1;
  }
};

const clearClientAuth = () => {
  api.setToken(null, null);
  useAccountStore.getState().clear();
  usePositionsStore.getState().clear();
};

const clearAuthState = (isLoading: boolean, error: string | null = null) => {
  pendingSessionHydration = null;
  clearClientAuth();
  useAuthStore.setState({
    user: null,
    profile: null,
    isAuthenticated: false,
    isLoading,
    error,
  });
};

const prepareSessionHydration = (session: Session, generation: number) => {
  const hydration: PendingSessionHydration = {
    generation,
    subject: session.user.id,
    session,
  };
  pendingSessionHydration = hydration;
  return hydration;
};

const hydrateAndCommitSession = async (
  hydration: PendingSessionHydration,
  fallbackUsername = 'trader',
  requireProfile = false
) => {
  assertCurrentAuthOperation(hydration.generation);

  try {
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', hydration.subject)
      .single();
    assertCurrentAuthOperation(hydration.generation);

    if (pendingSessionHydration !== hydration) {
      throw new AuthOperationSupersededError();
    }

    if (requireProfile && (profileError || !profile)) {
      throw new Error('Account created, but setup could not be loaded. Sign in to continue.');
    }

    const activeSession = hydration.session;
    const mappedProfile = profile ? mapProfile(profile as ProfileRow) : null;

    // Keep the API unauthenticated while the profile is loading. These two
    // synchronous commits are the only point where private requests resume.
    api.setToken(activeSession.access_token, activeSession.user.id);
    useAuthStore.setState({
      user: {
        id: activeSession.user.id,
        email: activeSession.user.email ?? '',
        username:
          mappedProfile?.username
          || activeSession.user.user_metadata.username
          || fallbackUsername,
        createdAt: activeSession.user.created_at,
      },
      profile: mappedProfile,
      isAuthenticated: true,
      isLoading: false,
      error: null,
    });
  } finally {
    if (pendingSessionHydration === hydration) {
      pendingSessionHydration = null;
    }
  }
};

const handleAuthStateChange = (event: AuthChangeEvent, session: Session | null) => {
  if (logoutEventBarrierCount > 0) {
    // A provider sign-in that started before logout can persist its session and
    // emit SIGNED_IN before its promise resolves. Logout owns every provider
    // event until those captured session creators settle and a final sign-out
    // has removed any late durable session.
    return;
  }

  const activeLocalOperation = localAuthOperation;
  if (
    activeLocalOperation
    && isCurrentAuthOperation(activeLocalOperation.generation)
  ) {
    // Supabase emits SIGNED_IN inside signIn/signUp and SIGNED_OUT inside
    // signOut. The initiating method owns those transitions. A sign-out during
    // sign-in is not expected and must still invalidate the local operation.
    if (activeLocalOperation.kind === 'logout') return;
    if (session?.access_token) return;
  }

  if (event === 'SIGNED_OUT' || !session?.access_token) {
    beginAuthOperation();
    clearAuthState(false);
    return;
  }

  const subject = session.user.id;
  const authState = useAuthStore.getState();

  if (
    authState.isAuthenticated
    && authState.user?.id === subject
  ) {
    // A refresh for the current subject rotates credentials without clearing
    // that user's account, positions, or in-flight private requests.
    api.setToken(session.access_token, subject);
    return;
  }

  if (pendingSessionHydration?.subject === subject) {
    pendingSessionHydration.session = session;
    return;
  }

  const operation = beginAuthOperation();
  clearAuthState(true);
  const hydration = prepareSessionHydration(session, operation);

  // Supabase warns against awaiting client calls inside the auth callback.
  // Defer the profile query until the callback has released its auth lock.
  queueMicrotask(() => {
    void hydrateAndCommitSession(hydration).catch((error) => {
      if (!isCurrentAuthOperation(operation)) return;
      console.error('Auth state change error:', error);
      clearAuthState(false, 'Authentication session could not be loaded');
    });
  });
};

export const useAuthStore = create<AuthStore>()(
  (set) => ({
      user: null,
      profile: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,

      initialize: async () => {
        const operation = beginAuthOperation();
        // Keep the check in this method: once the final queued barrier clears,
        // the intent check must run synchronously before another logout can queue.
        while (activeLogoutBarrier) await activeLogoutBarrier;
        if (!isCurrentAuthOperation(operation)) return;

        // Supabase owns the durable session. Never trust a previously derived
        // client-side user/profile while the authoritative session is unknown.
        clearClientAuth();
        set({ user: null, profile: null, isAuthenticated: false, isLoading: true, error: null });

        // React StrictMode can initialize twice in development. Keep exactly one
        // listener so refresh events never race or reconnect the socket twice.
        authSubscription?.unsubscribe();
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          handleAuthStateChange
        );
        authSubscription = subscription;

        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!isCurrentAuthOperation(operation)) return;
          
          if (session?.access_token) {
            const hydration = prepareSessionHydration(session, operation);
            await hydrateAndCommitSession(hydration);
          } else {
            clearAuthState(false);
          }
        } catch (error) {
          if (!isCurrentAuthOperation(operation)) return;
          console.error('Auth initialization error:', error);
          clearClientAuth();
          set({ user: null, profile: null, isAuthenticated: false, isLoading: false });
        }

      },

      login: async (username: string, password: string) => {
        // Claim intent before waiting: if a later logout queues, this login must
        // not start after that newer logout completes.
        const operation = beginLocalAuthOperation('login');
        const previousSessionCreatorBarrier = activeSessionCreatorBarrier;
        let releaseSessionCreatorBarrier!: () => void;
        const sessionCreatorBarrier = new Promise<void>((resolve) => {
          releaseSessionCreatorBarrier = resolve;
        });
        activeSessionCreatorBarrier = sessionCreatorBarrier;

        try {
          if (previousSessionCreatorBarrier) await previousSessionCreatorBarrier;
          while (activeLogoutBarrier) await activeLogoutBarrier;
          assertCurrentAuthOperation(operation);

          clearClientAuth();
          set({
            user: null,
            profile: null,
            isAuthenticated: false,
            isLoading: true,
            error: null,
          });
          try {
            if (previousSessionCreatorBarrier) {
              await clearPriorProviderSession(operation);
            }

            const email = usernameToEmail(username);
            const { data, error } = await trackSessionCreator(
              supabase.auth.signInWithPassword({
                email,
                password,
              })
            );
            assertCurrentAuthOperation(operation);
            releaseLocalAuthOperation(operation);

            if (error) {
              if (error.message.includes('Invalid login credentials')) {
                throw new Error('Invalid username or password');
              }
              throw error;
            }

            if (data.session) {
              const hydration = prepareSessionHydration(data.session, operation);
              await hydrateAndCommitSession(hydration, username);
            } else {
              throw new Error('Login did not create a session');
            }
          } catch (error) {
            releaseLocalAuthOperation(operation);
            if (!isCurrentAuthOperation(operation)) {
              throw new AuthOperationSupersededError();
            }
            clearClientAuth();
            set({
              user: null,
              profile: null,
              isAuthenticated: false,
              error: error instanceof Error ? error.message : 'Login failed',
              isLoading: false,
            });
            throw error;
          }
        } finally {
          releaseLocalAuthOperation(operation);
          if (activeSessionCreatorBarrier === sessionCreatorBarrier) {
            activeSessionCreatorBarrier = null;
          }
          releaseSessionCreatorBarrier();
        }
      },

      register: async (username: string, password: string) => {
        const operation = beginLocalAuthOperation('register');
        const previousSessionCreatorBarrier = activeSessionCreatorBarrier;
        let releaseSessionCreatorBarrier!: () => void;
        const sessionCreatorBarrier = new Promise<void>((resolve) => {
          releaseSessionCreatorBarrier = resolve;
        });
        activeSessionCreatorBarrier = sessionCreatorBarrier;

        try {
          if (previousSessionCreatorBarrier) await previousSessionCreatorBarrier;
          while (activeLogoutBarrier) await activeLogoutBarrier;
          assertCurrentAuthOperation(operation);

          clearClientAuth();
          set({
            user: null,
            profile: null,
            isAuthenticated: false,
            isLoading: true,
            error: null,
          });
          try {
            if (previousSessionCreatorBarrier) {
              await clearPriorProviderSession(operation);
            }

            if (!config.enableSyntheticEmailSignup) {
              throw new Error(
                'New account registration is disabled until username authentication is configured for this deployment.'
              );
            }

            const normalizedUsername = username.toLowerCase();
            const email = usernameToEmail(normalizedUsername);

            const { data, error } = await trackSessionCreator(
              supabase.auth.signUp({
                email,
                password,
                options: {
                  data: { username: normalizedUsername },
                },
              })
            );
            assertCurrentAuthOperation(operation);
            releaseLocalAuthOperation(operation);

            if (error) throw error;

            if (data.user) {
              // Supabase can obscure duplicate signups by returning a user with no
              // identities. Treat that as a username collision in this username-only UI.
              if (data.user.identities?.length === 0) {
                throw new Error('Username already taken');
              }

              if (data.session) {
                // The database trigger provisions profile, account, and leaderboard
                // rows in the same transaction as auth.users. Read the durable row;
                // never fabricate local state after a partial registration.
                const hydration = prepareSessionHydration(data.session, operation);
                await hydrateAndCommitSession(hydration, normalizedUsername, true);
              } else {
                throw new Error(
                  'Registration could not finish because this deployment requires email confirmation for an address that cannot receive mail.'
                );
              }
            } else {
              throw new Error('Registration did not create an account');
            }
          } catch (error) {
            releaseLocalAuthOperation(operation);
            if (!isCurrentAuthOperation(operation)) {
              throw new AuthOperationSupersededError();
            }
            clearClientAuth();
            set({
              user: null,
              profile: null,
              isAuthenticated: false,
              error: error instanceof Error ? error.message : 'Registration failed',
              isLoading: false,
            });
            throw error;
          }
        } finally {
          releaseLocalAuthOperation(operation);
          if (activeSessionCreatorBarrier === sessionCreatorBarrier) {
            activeSessionCreatorBarrier = null;
          }
          releaseSessionCreatorBarrier();
        }
      },

      logout: async () => {
        // Claim intent and the queue tail synchronously. This preserves call
        // order across login/register/logout actions waiting on older logouts.
        const operation = beginLocalAuthOperation('logout');
        const previousLogoutBarrier = activeLogoutBarrier;
        let releaseLogoutBarrier!: () => void;
        const logoutBarrier = new Promise<void>((resolve) => {
          releaseLogoutBarrier = resolve;
        });
        // Claim the tail synchronously so operations invoked later in this tick
        // queue behind this logout even while it waits for an earlier logout.
        activeLogoutBarrier = logoutBarrier;
        if (previousLogoutBarrier) await previousLogoutBarrier;

        const capturedSessionCreators = [...inFlightSessionCreators];
        logoutEventBarrierCount += 1;
        clearClientAuth();
        set({
          user: null,
          profile: null,
          isAuthenticated: false,
          isLoading: true,
          error: null,
        });
        try {
          let signOutError = await tryProviderSignOut();

          // The first sign-out clears the session that existed when logout was
          // requested. A sign-in/sign-up already in flight can recreate it, so
          // keep logout pending until every captured creator settles and then
          // clear the provider one final time.
          await Promise.allSettled(capturedSessionCreators);

          if (capturedSessionCreators.length > 0 || signOutError) {
            signOutError = await tryProviderSignOut();
          }
          if (!isCurrentAuthOperation(operation)) return;
          if (signOutError) throw signOutError;

          set({
            user: null,
            profile: null,
            isAuthenticated: false,
            isLoading: false,
          });
        } catch (error) {
          if (!isCurrentAuthOperation(operation)) return;
          clearClientAuth();
          set({
            user: null,
            profile: null,
            isAuthenticated: false,
            isLoading: false,
            error: error instanceof Error ? error.message : 'Logout failed',
          });
          throw error;
        } finally {
          logoutEventBarrierCount -= 1;
          releaseLocalAuthOperation(operation);
          if (activeLogoutBarrier === logoutBarrier) {
            activeLogoutBarrier = null;
          }
          releaseLogoutBarrier();
        }
      },

      setError: (error: string | null) => set({ error }),
  })
);
